import React from 'react'
import { useApp } from '../../context/AppContext'
import { ApiError, TimeOff, ValidationError, api } from '../../api/client'
import { todayIso } from '../../calendar/dates'
import { appointmentsInTimeOff, timeOffProblems, timeOffWarning, upcomingTimeOff } from '../../calendar/timeOff'
import { formatDateRange } from '../../catalog/format'
import Panel, { PanelStatus } from './Panel'
import Field from './Field'
import WeekEditor from '../../components/WeekEditor'
import { DraftWeek, errorsByWindow, sameWeek, toDraftWeek, toWindows, weekSummary } from './hoursForm'
import { saveErrorMessage, showToast } from './toast'

const emptyTimeOff = { name: '', starts_on: '', ends_on: '' }

export default function HoursPanel() {
  const { dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [week, setWeek] = React.useState<DraftWeek>(() => toDraftWeek([]))
  const [savedWeek, setSavedWeek] = React.useState<DraftWeek>(() => toDraftWeek([]))
  const [hoursErrors, setHoursErrors] = React.useState<Record<string, string>>({})
  const [savingHours, setSavingHours] = React.useState(false)
  const [hoursError, setHoursError] = React.useState('')

  const [timeOff, setTimeOff] = React.useState<TimeOff[]>([])
  const [draftOff, setDraftOff] = React.useState(emptyTimeOff)
  const [offErrors, setOffErrors] = React.useState<Record<string, string>>({})
  const [addingOff, setAddingOff] = React.useState(false)
  const [offError, setOffError] = React.useState('')
  const [removingId, setRemovingId] = React.useState<string | null>(null)
  // Her week shows as a summary (principle P3); the full editor opens on
  // demand, and straight away for a week with no hours yet.
  const [editing, setEditing] = React.useState(false)
  const [offFormOpen, setOffFormOpen] = React.useState(false)

  const load = React.useCallback(async () => {
    setStatus('loading')
    try {
      const [windows, off] = await Promise.all([api.getWorkingHours(), api.listTimeOff()])
      setWeek(toDraftWeek(windows))
      setSavedWeek(toDraftWeek(windows))
      setEditing(windows.length === 0)
      setTimeOff(off)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const saveHours = async () => {
    const { windows, positions, errors } = toWindows(week)
    setHoursError('')
    if (Object.keys(errors).length > 0) {
      setHoursErrors(errors)
      return
    }
    setSavingHours(true)
    setHoursErrors({})
    try {
      const saved = toDraftWeek(await api.putWorkingHours(windows))
      setWeek(saved)
      setSavedWeek(saved)
      setEditing(false)
      showToast(dispatch, 'Working hours saved')
    } catch (error) {
      if (error instanceof ValidationError) {
        setHoursErrors(errorsByWindow(error.fields, positions))
      } else {
        setHoursError(saveErrorMessage(error))
      }
    } finally {
      setSavingHours(false)
    }
  }

  // A warning naming the booked appointments inside the new time off; the
  // add waits for "Add time off anyway".
  const [offWarning, setOffWarning] = React.useState('')

  const addTimeOff = async (e: React.FormEvent | null, confirmed = false) => {
    e?.preventDefault()
    setOffErrors({})
    setOffError('')
    const today = todayIso()
    const problems = timeOffProblems(draftOff, timeOff, today)
    if (Object.keys(problems).length > 0) {
      setOffErrors(problems)
      return
    }
    setAddingOff(true)
    if (!confirmed) {
      const ends = draftOff.ends_on || draftOff.starts_on
      try {
        const inside = appointmentsInTimeOff(await api.listAppointments(draftOff.starts_on, ends), draftOff.starts_on, ends)
        if (inside.length > 0) {
          setOffWarning(timeOffWarning(inside))
          setAddingOff(false)
          return
        }
      } catch {
        // Couldn't check: add it anyway, as before; the calendar still shows both.
      }
    }
    setOffWarning('')
    try {
      const name = draftOff.name.trim()
      await api.addTimeOff({
        name: name === '' ? null : name,
        starts_on: draftOff.starts_on,
        ends_on: draftOff.ends_on || draftOff.starts_on,
      })
      // The add itself succeeded, so clear the draft and toast regardless of
      // whether the follow-up reload succeeds — a failed reload just leaves
      // the list stale, it is not a failed add.
      setDraftOff(emptyTimeOff)
      setOffFormOpen(false)
      showToast(dispatch, 'Time off added')
      try {
        setTimeOff(await api.listTimeOff())
      } catch {
        // Leave the existing list as-is; the add still succeeded.
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        setOffErrors(error.fields)
      } else {
        setOffError(saveErrorMessage(error))
      }
    } finally {
      setAddingOff(false)
    }
  }

  const removeTimeOff = async (off: TimeOff) => {
    if (removingId !== null) return
    setOffError('')
    setRemovingId(off.id)
    try {
      await api.deleteTimeOff(off.id)
      setTimeOff((current) => current.filter((o) => o.id !== off.id))
      showToast(dispatch, 'Time off removed')
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setTimeOff((current) => current.filter((o) => o.id !== off.id))
      } else {
        setOffError(saveErrorMessage(error))
      }
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <Panel
      title="Working hours and time off"
      unsavedChanges={status === 'ready' && !sameWeek(week, savedWeek)}
      intro="When clients can book you. Leave a gap between two stretches for a break.">
      {status !== 'ready' ? (
        <PanelStatus status={status} onRetry={load} />
      ) : (
        <>
          {editing ? (
            <>
              <WeekEditor week={week} onChange={setWeek} errors={hoursErrors} />
              {(hoursErrors.week || hoursError) && (
                <p className="field-error" role="alert">{hoursErrors.week ?? hoursError}</p>
              )}
              <div className="settings-actions">
                <button type="button" className="btn btn-primary" onClick={saveHours} disabled={savingHours}>
                  {savingHours ? 'Saving…' : 'Save working hours'}
                </button>
                {savedWeek.some((d) => d.length > 0) && (
                  <button type="button" className="btn btn-quiet" disabled={savingHours}
                    onClick={() => { setWeek(savedWeek); setHoursErrors({}); setHoursError(''); setEditing(false) }}>
                    Cancel
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <dl className="week-summary">
                {weekSummary(savedWeek).map((row) => (
                  <div key={row.days} className="week-summary__row">
                    <dt>{row.days}</dt>
                    <dd className={row.hours === 'Closed' ? 'week-summary__closed' : undefined}>{row.hours}</dd>
                  </div>
                ))}
              </dl>
              <button type="button" className="btn btn-secondary btn-compact settings-start" onClick={() => setEditing(true)}>Edit working hours</button>
            </>
          )}

          <h3 className="settings-subheading">Time off</h3>
          <p className="settings-intro">Days you’re away. Clients can’t book them.</p>

          {upcomingTimeOff(timeOff, todayIso()).length > 0 && (
            <ul className="time-off-list">
              {upcomingTimeOff(timeOff, todayIso()).map((off) => (
                <li key={off.id} className="time-off-item">
                  <div>
                    <div className="time-off-item__name">{off.name ?? 'Time off'}</div>
                    <div className="time-off-item__dates">{formatDateRange(off.starts_on, off.ends_on)}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-compact"
                    aria-label={`Remove ${off.name ?? 'time off'}, ${formatDateRange(off.starts_on, off.ends_on)}`}
                    onClick={() => removeTimeOff(off)}
                    disabled={removingId !== null}
                  >
                    {removingId === off.id ? 'Removing…' : 'Remove'}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {upcomingTimeOff(timeOff, todayIso()).length === 0 && !offFormOpen && <p className="settings-status">No time off booked.</p>}
          {!offFormOpen && (
            <button type="button" className="btn btn-secondary btn-compact settings-start" onClick={() => setOffFormOpen(true)}>Add time off</button>
          )}
          {offFormOpen && <form className="settings-form" onSubmit={addTimeOff} noValidate>
            <Field id="time-off-name" label="Name (optional)" error={offErrors.name}>
              <input
                id="time-off-name"
                placeholder="Christmas"
                value={draftOff.name}
                onChange={(e) => setDraftOff({ ...draftOff, name: e.target.value })}
              />
            </Field>
            <div className="field-pair">
              <Field id="time-off-start" label="First day" error={offErrors.starts_on}>
                <input
                  id="time-off-start"
                  type="date"
                  min={todayIso()}
                  value={draftOff.starts_on}
                  onChange={(e) => { setDraftOff({ ...draftOff, starts_on: e.target.value }); setOffWarning('') }}
                />
              </Field>
              <Field id="time-off-end" label="Last day" error={offErrors.ends_on}>
                <input
                  id="time-off-end"
                  type="date"
                  min={draftOff.starts_on || undefined}
                  value={draftOff.ends_on}
                  onChange={(e) => { setDraftOff({ ...draftOff, ends_on: e.target.value }); setOffWarning('') }}
                />
              </Field>
            </div>
            {offError && <p className="field-error" role="alert">{offError}</p>}
            {offWarning ? (
              <div className="time-off-warning" role="alert">
                <p>{offWarning}</p>
                <div className="settings-actions">
                  <button type="button" className="btn btn-primary btn-compact" disabled={addingOff} onClick={() => addTimeOff(null, true)}>
                    {addingOff ? 'Adding…' : 'Add time off anyway'}
                  </button>
                  <button type="button" className="btn btn-quiet btn-compact" disabled={addingOff} onClick={() => setOffWarning('')}>
                    Change dates
                  </button>
                </div>
              </div>
            ) : (
              <div className="settings-actions">
                <button type="submit" className="btn btn-primary btn-compact" disabled={addingOff}>
                  {addingOff ? 'Adding…' : 'Add time off'}
                </button>
                <button type="button" className="btn btn-quiet btn-compact" disabled={addingOff}
                  onClick={() => { setOffFormOpen(false); setDraftOff(emptyTimeOff); setOffErrors({}); setOffError(''); setOffWarning('') }}>Cancel</button>
              </div>
            )}
          </form>}
        </>
      )}
    </Panel>
  )
}
